import { useEffect, useState } from 'react';
import { useLanguageContextAPI } from '../../context/LanguageContext';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import {
  // XMLSvgComp,
  ExcelSvgComp,
  CsvSvgComp,
} from './Icons/SvgComp';
import { useUserContextApi } from '../../context/UserContext';
import { useColumnContextAPI } from '../../context/GetAllColumnContext';
import exportFunctions from './exportFunctions';
import { Accordion, Collapsible, Button, Tooltip } from '@airbus/components-react';
import styles from './ExcelExport.module.css';
import { Send } from '@airbus/icons/react';
import { useConfigContext } from '../../context/ConfigContext';
import DurationSelectorDialog from './DurationSelectorDialog';
import { produce } from 'immer';
import { formatDate } from '../../utils';
import { useSvcWorkerContext } from '../../context/ServiceWorkerContext';
import { getApiBaseUrl } from '../../utils/apiBase';

export function ExportMenuItem(props) {
  const apiRef = useGridApiContext();
  const { language } = useLanguageContextAPI();
  const { userDetails } = useUserContextApi();
  const { configSchema } = useConfigContext();
  const { allRows, tableDetails, allDropdowns } = useColumnContextAPI();
  const { hideMenu, setAlertbar, setExportInProgress } = props;
  const [records, setRecords] = useState([]);
  const [config, setConfig] = useState({
    role_id: userDetails.roleIds,
    sheetName: [userDetails.appDetails[`screenDisplayName${language}`]],
    tableName: [
      tableDetails?.mbdDataGrid.schemaName + '_' + tableDetails?.mbdDataGrid.dataGridSource,
    ],
    tableNameManual: [tableDetails?.mbdDataGrid.dataGridSource], // for Manual MoveIT Postgres Table Fetch
    dataSchema: tableDetails?.mbdDataGrid.schemaName,
    dataSource: tableDetails?.mbdDataGrid.dataGridSource,
    dataSourceUniqueIdentifier: tableDetails?.mbdDataGrid.dataGridUniqueIdentifier,
  });
  const apiBase = getApiBaseUrl();
  useEffect(() => {
    setConfig((prestate) => ({
      ...prestate,
      sheetName: [userDetails?.appDetails[`screenDisplayName${language}`]],
    }));
  }, [language]);
  useEffect(() => {
    fetch(`${apiBase}/api/reports?screenGridId=${tableDetails.id}`)
      .then((res) => res.json())
      .then((data) => {
        setRecords(data?.[`${configSchema}_mbd_roles_to_screen_grid_reports`] || []);
      });
  }, []);
  return (
    <ShowExportMenuItem
      apiRef={apiRef}
      allRows={allRows}
      records={records}
      hideMenu={hideMenu}
      config={config}
      language={language}
      allDropdowns={allDropdowns}
      tableDetails={tableDetails}
      configSchema={configSchema}
      setAlertbar={setAlertbar}
      setExportInProgress={setExportInProgress}
    />
  );
}

type ReportStateType = {
  durationDialogOpen: boolean;
  exportType: string;
  reportInfo: any;
};

export function ShowExportMenuItem(props) {
  const {
    apiRef,
    records,
    hideMenu,
    config,
    language,
    allDropdowns,
    configSchema,
    setAlertbar,
    setExportInProgress,
  } = props;

  const [reportState, setReportState] = useState<ReportStateType>({
    durationDialogOpen: false,
    exportType: null,
    reportInfo: null,
  });
  const { t } = useLanguageContextAPI();
  const { fetchReport } = useSvcWorkerContext();
  const { allAccessibleColumns, customConfig } = useColumnContextAPI();
  const { AdditionalReportFilterModel } = customConfig;

  const handleExport = async (exportType: string, record = null) => {
    setExportInProgress(true);
    try {
      if (exportType === 'xlsx') {
        return exportFunctions.handleExport({
          apiRef,
          config,
          language,
          allDropdowns,
          configSchema,
          fetchReport,
          fileType: 'xlsx',
          allAccessibleColumns,
          AdditionalReportFilterModel,
        });
      } else if (exportType === 'csv') {
        return exportFunctions.handleExport({
          apiRef,
          config,
          language,
          allDropdowns,
          configSchema,
          fetchReport,
          fileType: 'csv',
          allAccessibleColumns,
          AdditionalReportFilterModel,
        });
      } else if (exportType === 'manualMoveIT') {
        await exportFunctions.sendToMoveIt({
          record,
          configSchema,
        });
        setAlertbar({
          children: t('export_report.success', {
            msg: record.mbdReportConfig.report_name,
          }),
          severity: 'success',
        });
      } else {
        const fileNameObj = {
          reportName: record.mbdReportConfig.reportName,
          exportReportName: record.mbdReportConfig.exportReportName,
        };
        return exportFunctions.handleCustomExport({
          exportType,
          fileNameObj,
          language,
          record,
          fetchReport,
        });
      }
    } catch (e) {
      if (e instanceof SyntaxError) {
        setAlertbar({
          children: t('error.exception', { msg: 'Gateway timeout' }),
          severity: 'error',
        });
      } else {
        setAlertbar({
          children: t('error.error_msg', { msg: e.message }),
          severity: 'error',
        });
      }
    } finally {
      setExportInProgress(false);
      hideMenu?.();
    }
  };

  const showDurationDialog = (exportType: string, record) => {
    const calcNewState = produce((draft) => {
      draft.durationDialogOpen = true;
      draft.exportType = exportType;
      draft.reportInfo = record;
    });
    setReportState(calcNewState);
  };

  const handleDurationDialogClose = () => {
    const calcNewState = produce((draft) => {
      draft.durationDialogOpen = false;
      draft.exportType = null;
      draft.reportInfo = null;
    });
    setReportState(calcNewState);
  };

  const handleExportWithDuration = async (duration) => {
    const { reportInfo, exportType } = reportState;
    const fileNameObj = {
      reportName: reportInfo.mbdReportConfig.reportName,
      exportReportName: reportInfo.mbdReportConfig.exportReportName,
    };
    setReportState(
      produce((draft) => {
        draft.durationDialogOpen = false;
        draft.exportType = null;
        draft.reportInfo = null;
      }),
    );
    try {
      return exportFunctions.handleCustomExport({
        from: formatDate(duration.from),
        to: formatDate(duration.to),
        exportType: exportType,
        fileNameObj,
        language,
        record: reportInfo,
        fetchReport,
      });
    } catch (error) {
      setAlertbar({
        children: t('error.error_msg', { msg: error.message }),
        severity: 'error',
      });
    } finally {
      setExportInProgress(false);
      hideMenu?.();
    }
  };

  return (
    <div className={styles.exportMenuContainer}>
      <Accordion className={styles.exportAccordion} single defaultIndex={[0]}>
        <Collapsible title={t('export_screen_table')}>
          <Button
            onClick={() => handleExport('xlsx')}
            className={styles.iconsSvg}
            data-testid='forExcel'
          >
            <ExcelSvgComp />
          </Button>
          <Button onClick={() => handleExport('csv')} className={styles.iconsSvg}>
            <CsvSvgComp />
          </Button>
        </Collapsible>
        {records?.map((record) => (
          <Collapsible
            key={record.reportId}
            title={record.mbdReportConfig[`reportName${language}`]}
            aria-controls='panel1d-content'
            id='panel1d-header'
          >
            <div>
              <Button
                onClick={() => {
                  const fn =
                    record.mbdReportConfig.reportName === 'PAG_NPI_MoB_MoM'
                      ? showDurationDialog
                      : handleExport;
                  fn('use case2 for excel', record);
                }}
                className={styles.iconsSvg}
              >
                <ExcelSvgComp />
              </Button>
              <Button
                onClick={() => {
                  const fn =
                    record.mbdReportConfig.reportName === 'PAG_NPI_MoB_MoM'
                      ? showDurationDialog
                      : handleExport;
                  fn('use case2 for csv', record);
                }}
                className={styles.iconsSvg}
              >
                <CsvSvgComp />
              </Button>
              {record.mbdReportConfig?.reportMode?.includes('Manual MoveIT') && (
                <Button
                  className={styles.iconsSvg}
                  onClick={() => handleExport('manualMoveIT', record)}
                >
                  <Tooltip label={t('export_report.send')}>
                    <Send style={{ marginTop: '0.5rem' }} />
                  </Tooltip>
                </Button>
              )}
            </div>
          </Collapsible>
        ))}
      </Accordion>
      <DurationSelectorDialog
        open={reportState.durationDialogOpen}
        onClose={handleDurationDialogClose}
        onSelect={handleExportWithDuration}
      />
    </div>
  );
}
